import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Toolbar,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import { I18nContext } from 'translations';
import { get } from 'api/requests';
import { urlGroupsCompanies } from 'api';

const ModalSedes = ({ open, onClose, companyId }) => {
  const { translate } = useContext(I18nContext);
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && companyId) {
      setLoading(true);
      get(`${urlGroupsCompanies}/${companyId}/areas?all=true`, {})
        .then(({ data }) => {
          setSedes(Array.isArray(data) ? data : (data.data || []));
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
    if (!open) setSearch('');
  }, [open, companyId]);

  const filtered = useMemo(
    () => sedes.filter((s) =>
      s.name?.toLowerCase().includes(search.toLowerCase()) ||
      s.address?.toLowerCase().includes(search.toLowerCase())
    ),
    [sedes, search]
  );

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <Toolbar style={{ justifyContent: 'center', borderBottom: '1px solid #f0f0f0' }}>
        <IconButton
          color="primary"
          onClick={onClose}
          aria-label="close"
          style={{ position: 'absolute', right: 20, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
        <div style={{ textAlign: 'center' }}>
          <Typography variant="h6" color="primary">
            {translate('app.settings.groups')}
          </Typography>
          {!loading && (
            <Typography variant="caption" color="textSecondary">
              {sedes.length} {translate('app.settings.groups.table.group')}{sedes.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </div>
      </Toolbar>

      <DialogContent style={{ padding: '16px 24px 24px', height: 480, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress color="primary" size={32} />
          </div>
        ) : sedes.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9e9e9e' }}>
            <LocationOnOutlinedIcon style={{ fontSize: 48, marginBottom: 8, opacity: 0.4 }} />
            <Typography variant="body2" color="textSecondary">
              {translate('thereIsNoData')}
            </Typography>
          </div>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={translate('search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginBottom: 12, flexShrink: 0, width: '50%', alignSelf: 'flex-end' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#9e9e9e', fontSize: '1.2rem' }} />
                  </InputAdornment>
                )
              }}
            />
            <TableContainer style={{ flex: 1, overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    {translate('noMatches')}
                  </Typography>
                </div>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><b>{translate('app.settings.groups.table.group')}</b></TableCell>
                      <TableCell><b>{translate('app.settings.groups.table.address')}</b></TableCell>
                      <TableCell align="right"><b>Lat</b></TableCell>
                      <TableCell align="right"><b>Lon</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((sede) => (
                      <TableRow hover key={sede._id}>
                        <TableCell>{sede.name || '-'}</TableCell>
                        <TableCell>{sede.address || '-'}</TableCell>
                        <TableCell align="right" style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#555' }}>
                          {sede.lat != null ? Number(sede.lat).toFixed(6) : '-'}
                        </TableCell>
                        <TableCell align="right" style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#555' }}>
                          {sede.lon != null ? Number(sede.lon).toFixed(6) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalSedes;
